import { useState } from 'react'
import { Printer } from 'lucide-react'
import { Button } from '@/components/ui/button'
import apiClient from '@/api/client'
import { toastHelpers } from '@/lib/toast-helpers'
import { useCurrency } from '@/contexts/CurrencyContext'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { printZReport } from '@/lib/printZReport'

interface ZReportButtonProps {
  dayId: string
  /** Day must be staged or closed for the Z slip to make sense. */
  enabled: boolean
  /** Visual variant: "default" for primary action, "ghost" for reprint rows. */
  variant?: 'default' | 'outline' | 'ghost'
  size?: 'sm' | 'default' | 'lg'
  label?: string
}

/**
 * Triggers the Z report print: fetches the bundled payload, fetches receipt
 * branding settings, and dispatches an off-screen iframe print job. Stable
 * across reprints — the backend assigns a monotonic Z number once and
 * returns it on every subsequent call.
 */
export function ZReportButton({
  dayId,
  enabled,
  variant = 'outline',
  size = 'lg',
  label = 'Print Z report',
}: ZReportButtonProps) {
  const { formatCurrency } = useCurrency()
  const [busy, setBusy] = useState(false)

  const handleClick = async () => {
    if (!enabled || busy) return
    setBusy(true)
    try {
      const [zRes, settingsRes] = await Promise.all([
        apiClient.getZReport(dayId),
        apiClient.getAllSettings(),
      ])
      if (!zRes.success || !zRes.data) {
        toastHelpers.error(zRes.message || 'Failed to load Z report')
        return
      }
      if (!settingsRes.success || !settingsRes.data) {
        toastHelpers.error(settingsRes.message || 'Failed to load receipt settings')
        return
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const { printed } = printZReport(zRes.data, cfg, formatCurrency)
      if (!printed) {
        toastHelpers.error('Print failed — check printer connection and pop-up settings')
      }
    } catch (err) {
      toastHelpers.apiError('Print Z report', err as Error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={!enabled || busy}
    >
      <Printer className="mr-2 h-4 w-4" />
      {busy ? 'Preparing…' : label}
    </Button>
  )
}
